import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Container,
  Grid,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Stack
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  GET_SPLIT_TEST_CONFIG,
  UPDATE_SPLIT_TEST_CONFIG
} from 'src/graphql/queries';

/**
 * Split test variant configuration
 */
interface SplitTestVariant {
  url: string; // url to be split
  // Percentage of traffic to redirect (0-100, set to 0 to disable)
  splitTestPercentage: number;
  // If true, will only log the redirect without actually redirecting
  logOnly: boolean;

  controlLabel?: string;

  // If true, all traffic will be part of the test not just from ads
  allTraffic?: boolean;
  // Array of multiple test variants (mutually exclusive with url/label)
  tests?: Array<{
    url: string;
    label: string;
    splitTestPercentage: number;
  }>;
}

/**
 * A/B Split Test Configuration
 */
export interface SplitTestConfig {
  // Default label for control group (users who don't get redirected)
  controlLabel?: string;

  // Map of original URLs to alternate test URLs with custom labels
  // Example: { "/store": { url: "/store-v2", label: "new-checkout", splitTestPercentage: 50, logOnly: false } }
  urlMappings: SplitTestVariant[];
}

interface MappingDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (originalUrl: string, variant: SplitTestVariant) => void;
  initialUrl?: string;
  initialVariant?: SplitTestVariant;
  mode: 'view' | 'edit' | 'add';
}

const MappingDialog: React.FC<MappingDialogProps> = ({
  open,
  onClose,
  onSave,
  initialUrl = '',
  initialVariant,
  mode
}) => {
  const [originalUrl, setOriginalUrl] = useState(initialUrl);
  const [experimentNumber, setExperimentNumber] = useState(1);
  const [pageName, setPageName] = useState('');
  const [logOnly, setLogOnly] = useState(initialVariant?.logOnly || false);
  const [allTraffic, setAllTraffic] = useState(
    initialVariant?.allTraffic || false
  );
  const [tests, setTests] = useState<
    Array<{ url: string; variantNumber: number; splitTestPercentage: number }>
  >([{ url: '', variantNumber: 1, splitTestPercentage: 50 }]);

  // Helper to parse experiment and variant numbers from existing label
  const parseLabel = (
    label: string
  ): { experimentNumber: number; variantNumber: number } | null => {
    const match = label.match(/^experiment(\d+)-variant(\d+)-formula$/);
    if (match) {
      return {
        experimentNumber: parseInt(match[1], 10),
        variantNumber: parseInt(match[2], 10)
      };
    }
    return null;
  };

  // Helper to parse control label
  const parseControlLabel = (
    controlLabel: string
  ): { experimentNumber: number; pageName: string } | null => {
    const match = controlLabel.match(/^experiment(\d+)-control-(.+)$/);
    if (match) {
      return {
        experimentNumber: parseInt(match[1], 10),
        pageName: match[2]
      };
    }
    return null;
  };

  // Helper to generate label from experiment and variant numbers
  const generateLabel = (expNum: number, varNum: number): string => {
    return `experiment${expNum}-variant${varNum}-formula`;
  };

  // Helper to generate control label
  const generateControlLabel = (expNum: number, pageName: string): string => {
    return `experiment${expNum}-control-${pageName}`;
  };

  // Reset form state when dialog opens or initial data changes
  useEffect(() => {
    if (open) {
      setOriginalUrl(initialUrl);
      setLogOnly(initialVariant?.logOnly || false);
      setAllTraffic(initialVariant?.allTraffic || false);

      if (initialVariant?.controlLabel) {
        // Parse control label to extract experiment number and page name
        const parsed = parseControlLabel(initialVariant.controlLabel);
        if (parsed) {
          setExperimentNumber(parsed.experimentNumber);
          setPageName(parsed.pageName);
        }
      }

      if (initialVariant?.tests && initialVariant.tests.length > 0) {
        // Parse existing labels to extract experiment and variant numbers
        const firstLabel = parseLabel(initialVariant.tests[0].label);
        if (firstLabel && !initialVariant.controlLabel) {
          setExperimentNumber(firstLabel.experimentNumber);
        }

        const parsedTests = initialVariant.tests.map((test) => {
          const parsed = parseLabel(test.label);
          return {
            url: test.url,
            variantNumber: parsed?.variantNumber || 1,
            splitTestPercentage: test.splitTestPercentage
          };
        });
        setTests(parsedTests);
      } else {
        setExperimentNumber(1);
        setPageName('');
        setTests([{ url: '', variantNumber: 1, splitTestPercentage: 50 }]);
      }
    }
  }, [open, initialUrl, initialVariant]);

  // Calculate total split percentage from all test variants
  const totalSplitPercentage = tests.reduce(
    (sum, test) => sum + (test.splitTestPercentage || 0),
    0
  );

  // Validation functions
  const isValidUrl = (url: string): boolean => {
    if (!url || url.trim() === '') return false;
    try {
      new URL(url);
      return url.startsWith('https://');
    } catch {
      return false;
    }
  };

  const getUrlError = (url: string): string => {
    if (!url || url.trim() === '') return 'URL is required';
    if (!url.startsWith('https://')) return 'URL must start with https://';
    try {
      new URL(url);
      return '';
    } catch {
      return 'Invalid URL format';
    }
  };

  const getVariantNumberError = (
    variantNumber: number,
    currentIndex: number
  ): string => {
    if (!variantNumber || variantNumber < 1)
      return 'Variant number must be a positive integer';
    if (!Number.isInteger(variantNumber))
      return 'Variant number must be an integer';
    const duplicateIndex = tests.findIndex(
      (test, idx) =>
        idx !== currentIndex && test.variantNumber === variantNumber
    );
    if (duplicateIndex !== -1) return 'Variant number must be unique';
    return '';
  };

  const getExperimentNumberError = (expNum: number): string => {
    if (!expNum || expNum < 1)
      return 'Experiment number must be a positive integer';
    if (!Number.isInteger(expNum))
      return 'Experiment number must be an integer';
    return '';
  };

  const getPageNameError = (name: string): string => {
    if (!name || name.trim() === '') return 'Page name is required';
    if (!/^[a-zA-Z0-9-_]+$/.test(name))
      return 'Page name can only contain letters, numbers, hyphens, and underscores';
    return '';
  };

  // Check if form is valid
  const isFormValid = (): boolean => {
    if (!originalUrl || originalUrl.trim() === '') return false;
    if (totalSplitPercentage > 100) return false;
    if (getExperimentNumberError(experimentNumber) !== '') return false;
    if (getPageNameError(pageName) !== '') return false;

    for (let i = 0; i < tests.length; i++) {
      const test = tests[i];
      if (!isValidUrl(test.url)) return false;
      if (getVariantNumberError(test.variantNumber, i) !== '') return false;
    }

    return true;
  };

  const handleSave = () => {
    if (!isFormValid()) return;

    // Generate labels from experiment and variant numbers
    const testsWithLabels = tests.map((test) => ({
      url: test.url,
      label: generateLabel(experimentNumber, test.variantNumber),
      splitTestPercentage: test.splitTestPercentage
    }));

    const variant: SplitTestVariant = {
      url: originalUrl,
      splitTestPercentage: totalSplitPercentage,
      logOnly,
      allTraffic,
      controlLabel: generateControlLabel(experimentNumber, pageName),
      tests: testsWithLabels
    };

    onSave(originalUrl, variant);
    onClose();
  };

  const handleAddTest = () => {
    const maxVariantNumber =
      tests.length > 0 ? Math.max(...tests.map((t) => t.variantNumber)) : 0;
    setTests([
      ...tests,
      { url: '', variantNumber: maxVariantNumber + 1, splitTestPercentage: 50 }
    ]);
  };

  const handleRemoveTest = (index: number) => {
    setTests(tests.filter((_, i) => i !== index));
  };

  const handleTestChange = (index: number, field: string, value: any) => {
    const newTests = [...tests];
    newTests[index] = { ...newTests[index], [field]: value };
    setTests(newTests);
  };

  const isViewMode = mode === 'view';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'add'
          ? 'Add URL Mapping'
          : mode === 'edit'
          ? 'Edit URL Mapping'
          : 'View URL Mapping'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <TextField
            label="Original URL"
            value={originalUrl}
            onChange={(e) => setOriginalUrl(e.target.value)}
            fullWidth
            disabled={mode === 'edit' || isViewMode}
            required
            error={!isViewMode && !!originalUrl && originalUrl.trim() === ''}
            helperText={
              !isViewMode && originalUrl.trim() === ''
                ? 'Original URL is required'
                : 'Can be a relative URL (e.g., /landing-page)'
            }
          />

          <TextField
            label="Experiment Number"
            type="number"
            value={experimentNumber}
            onChange={(e) => setExperimentNumber(Number(e.target.value))}
            fullWidth
            disabled={isViewMode}
            required
            error={
              !isViewMode && getExperimentNumberError(experimentNumber) !== ''
            }
            helperText={
              !isViewMode && getExperimentNumberError(experimentNumber)
                ? getExperimentNumberError(experimentNumber)
                : 'Positive integer for the experiment'
            }
            inputProps={{ min: 1, step: 1 }}
          />

          <TextField
            label="Page Name"
            value={pageName}
            onChange={(e) => setPageName(e.target.value)}
            fullWidth
            disabled={isViewMode}
            required
            error={!isViewMode && getPageNameError(pageName) !== ''}
            helperText={
              !isViewMode && getPageNameError(pageName)
                ? getPageNameError(pageName)
                : `Control label: ${
                    pageName
                      ? generateControlLabel(experimentNumber, pageName)
                      : 'experiment#-control-pagename'
                  }`
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={logOnly}
                onChange={(e) => setLogOnly(e.target.checked)}
                disabled={isViewMode}
              />
            }
            label="Log Only (Don't Actually Redirect)"
          />

          <FormControlLabel
            control={
              <Switch
                checked={allTraffic}
                onChange={(e) => setAllTraffic(e.target.checked)}
                disabled={isViewMode}
              />
            }
            label="All Traffic (Not Just Ads)"
          />

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                color: totalSplitPercentage > 100 ? 'error.main' : 'inherit'
              }}
            >
              Test Variants (Total: {totalSplitPercentage}%)
              {totalSplitPercentage > 100 && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ ml: 1, color: 'error.main' }}
                >
                  - Total cannot exceed 100%
                </Typography>
              )}
            </Typography>
            {tests.map((test, index) => (
              <Paper key={index} sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      label="Test URL"
                      value={test.url}
                      onChange={(e) =>
                        handleTestChange(index, 'url', e.target.value)
                      }
                      fullWidth
                      disabled={isViewMode}
                      error={
                        !isViewMode &&
                        !!test.url &&
                        getUrlError(test.url) !== ''
                      }
                      helperText={
                        !isViewMode && test.url
                          ? getUrlError(test.url) ||
                            'Must be a full URL with https://'
                          : 'Must be a full URL with https://'
                      }
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Variant Number"
                      type="number"
                      value={test.variantNumber}
                      onChange={(e) =>
                        handleTestChange(
                          index,
                          'variantNumber',
                          Number(e.target.value)
                        )
                      }
                      fullWidth
                      disabled={isViewMode}
                      error={
                        !isViewMode &&
                        getVariantNumberError(test.variantNumber, index) !== ''
                      }
                      helperText={
                        !isViewMode
                          ? getVariantNumberError(test.variantNumber, index) ||
                            `Label: ${generateLabel(
                              experimentNumber,
                              test.variantNumber
                            )}`
                          : `Label: ${generateLabel(
                              experimentNumber,
                              test.variantNumber
                            )}`
                      }
                      required
                      inputProps={{ min: 1, step: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="% Traffic"
                      type="number"
                      value={test.splitTestPercentage}
                      onChange={(e) =>
                        handleTestChange(
                          index,
                          'splitTestPercentage',
                          Number(e.target.value)
                        )
                      }
                      fullWidth
                      disabled={isViewMode}
                      inputProps={{ min: 0, max: 100 }}
                    />
                  </Grid>
                  {!isViewMode && (
                    <Grid item xs={12} sm={1}>
                      <IconButton
                        onClick={() => handleRemoveTest(index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            ))}
            {!isViewMode && (
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddTest}
                variant="outlined"
              >
                Add Test Variant
              </Button>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!isViewMode && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!isFormValid()}
          >
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const SplitTest: React.FC<{}> = () => {
  const [config, setConfig] = useState<SplitTestConfig | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit' | 'add'>('add');

  const [getConfig, { loading, error, data }] = useLazyQuery(
    GET_SPLIT_TEST_CONFIG
  );

  const [
    executeUpdateConfig,
    { loading: updateLoading, data: updateData, error: updateError }
  ] = useMutation(UPDATE_SPLIT_TEST_CONFIG);

  // Fetch config on mount
  useEffect(() => {
    getConfig();
  }, []);

  // Update config when data is fetched
  useEffect(() => {
    if (data?.GetSplitTestConfigs) {
      // Create a deep copy without __typename
      const cleanConfig: SplitTestConfig = {
        controlLabel: data.GetSplitTestConfigs.controlLabel,
        urlMappings: data.GetSplitTestConfigs.urlMappings.map(
          (mapping: any) => ({
            url: mapping.url,
            splitTestPercentage: mapping.splitTestPercentage,
            logOnly: mapping.logOnly,
            allTraffic: mapping.allTraffic,
            controlLabel: mapping.controlLabel,
            tests: mapping.tests?.map((test: any) => ({
              url: test.url,
              label: test.label,
              splitTestPercentage: test.splitTestPercentage
            }))
          })
        )
      };
      setConfig(cleanConfig);
    }
  }, [data]);

  const handleAddMapping = () => {
    setSelectedUrl(null);
    setDialogMode('add');
    setDialogOpen(true);
  };

  const handleEditMapping = (url: string) => {
    setSelectedUrl(url);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleViewMapping = (url: string) => {
    setSelectedUrl(url);
    setDialogMode('view');
    setDialogOpen(true);
  };

  const saveConfig = async (newConfig: SplitTestConfig) => {
    executeUpdateConfig({
      variables: { input: newConfig }
    });
  };

  const handleDeleteMapping = (url: string) => {
    const newMappings = config.urlMappings.filter(
      (mapping) => mapping.url !== url
    );
    const newConfig = { ...config, urlMappings: newMappings };

    saveConfig(newConfig);
    setConfig(newConfig);
  };

  const handleSaveMapping = (
    originalUrl: string,
    variant: SplitTestVariant
  ) => {
    let newMappings = [...config.urlMappings];

    if (selectedUrl) {
      // Editing existing mapping
      const index = newMappings.findIndex((m) => m.url === selectedUrl);
      if (index !== -1) {
        newMappings[index] = variant;
      }
    } else {
      // Adding new mapping
      newMappings.push(variant);
    }
    const newConfig = { ...config, urlMappings: newMappings };

    saveConfig(newConfig);

    setConfig(newConfig);
  };

  if (loading || !config || updateLoading) {
    return (
      <Card sx={{ width: '100%', mb: 4 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400
            }}
          >
            <Typography variant="body1">
              {updateLoading
                ? 'Updating split test configuration...'
                : 'Loading split test configuration...'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error || updateError) {
    return (
      <Card sx={{ width: '100%', mb: 4 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400
            }}
          >
            <Typography variant="body1" color="error">
              Error loading configuration:{' '}
              {error?.message || updateError?.message}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ width: '100%', mb: 4 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3
          }}
        >
          <Typography variant="h5" component="h2">
            Split Test Configuration
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddMapping}
          >
            Add URL Mapping
          </Button>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" sx={{ mb: 2 }}>
          URL Mappings
        </Typography>

        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Original URL</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Traffic Type</TableCell>
                <TableCell>Split %</TableCell>
                <TableCell>Variants</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {config.urlMappings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No URL mappings configured. Click "Add URL Mapping" to
                      create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                config.urlMappings.map((variant) => (
                  <TableRow key={variant.url}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {variant.url}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={variant.logOnly ? 'Log Only' : 'Active'}
                        color={variant.logOnly ? 'warning' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={variant.allTraffic ? 'All Traffic' : 'Ads Only'}
                        color={variant.allTraffic ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{variant.splitTestPercentage}%</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {variant.tests?.length || 0} variants
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleViewMapping(variant.url)}
                        title="View details"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditMapping(variant.url)}
                        title="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteMapping(variant.url)}
                        color="error"
                        title="Delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <MappingDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSave={handleSaveMapping}
          initialUrl={selectedUrl || ''}
          initialVariant={
            selectedUrl
              ? config.urlMappings.find((m) => m.url === selectedUrl)
              : undefined
          }
          mode={dialogMode}
        />
      </CardContent>
    </Card>
  );
};
